import type { StoryCtx } from './ctx.js';
import { spansIn, stripSpans, type SpanRole } from './spans.js';
import type { Entity } from './types.js';

/** Gültiger Verweisschlüssel (ohne „@“): Kleinbuchstabe am Anfang, dann Kleinbuchstaben, Ziffern, Unterstrich. */
export const REF_KEY = /^[a-z][a-z0-9_]{0,29}$/;
export const isValidRefKey = (k: string): boolean => REF_KEY.test(k);

const TRANSLIT: Record<string, string> = { ä: 'ae', ö: 'oe', ü: 'ue', ß: 'ss' };

/** Aus einem Namen einen Verweisschlüssel machen („Die Berghütte“ → „die_berghuette“). */
export function slugRef(name: string): string {
  const base = name
    .toLowerCase()
    .replace(/[äöüß]/g, (c) => TRANSLIT[c]!)
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 30)
    .replace(/_+$/, '');
  if (base === '') return 'element';
  return /^[a-z]/.test(base) ? base : `x${base}`.slice(0, 30);
}

/** Schlüssel, der in `taken` noch nicht vorkommt (hängt bei Bedarf eine Zahl an). */
export function uniqueRefKey(taken: ReadonlySet<string>, base: string): string {
  const root = isValidRefKey(base) ? base : slugRef(base);
  if (!taken.has(root)) return root;
  for (let i = 2; ; i++) {
    const suffix = String(i);
    const candidate = `${root.slice(0, 30 - suffix.length)}${suffix}`;
    if (!taken.has(candidate)) return candidate;
  }
}

export interface Mention {
  /** Schlüssel in Kleinbuchstaben, ohne „@“ */
  key: string;
  /** Position des „@“ im Text */
  index: number;
  /** Länge samt „@“ */
  length: number;
}

const MENTION = /@([A-Za-z][A-Za-z0-9_]{0,29})/g;

/** Alle Verweise `@schlüssel` in einem Text. Adressen wie „a@b.de“ zählen nicht (davor steht ein Wortzeichen). */
export function mentionsIn(text: string): Mention[] {
  const out: Mention[] = [];
  for (const m of text.matchAll(MENTION)) {
    const index = m.index ?? 0;
    if (index > 0 && /[\w@]/.test(text[index - 1]!)) continue;
    out.push({ key: m[1]!.toLowerCase(), index, length: m[0].length });
  }
  return out;
}

/** Benennt Verweise im Text um (`@alt` → `@neu`). */
export function renameRefInText(text: string, from: string, to: string): string {
  let out = '';
  let last = 0;
  for (const m of mentionsIn(text)) {
    if (m.key !== from) continue;
    out += text.slice(last, m.index) + `@${to}`;
    last = m.index + m.length;
  }
  return last === 0 ? text : out + text.slice(last);
}

/** Alle Schlüssel eines Eintrags: der Hauptschlüssel und die weiteren Bezeichnungen. */
export const keysOf = (e: Pick<Entity, 'refKey' | 'aliases'>): string[] => [e.refKey, ...(e.aliases ?? [])];

/** Einträge nach Verweisschlüssel (Hauptschlüssel und weitere Bezeichnungen). */
export const refIndex = (ctx: StoryCtx): Map<string, Entity> => new Map(ctx.story.entities.flatMap((e) => keysOf(e).map((k) => [k, e] as const)));

/** Alle vergebenen Schlüssel des Projekts, außer denen des Eintrags `exceptId`. */
export const takenRefKeys = (ctx: StoryCtx, exceptId?: string): Set<string> => new Set(ctx.story.entities.filter((e) => e.id !== exceptId).flatMap(keysOf));

/**
 * Prüft einen neuen Schlüssel: gültig und noch frei (auch nicht unter den weiteren Bezeichnungen anderer Einträge).
 * `own` sind die übrigen Schlüssel desselben Eintrags, die ebenfalls nicht doppelt vorkommen dürfen. Liefert eine Meldung oder null.
 */
export function checkRefKey(ctx: StoryCtx, key: string, exceptId?: string, own: string[] = []): string | null {
  if (!isValidRefKey(key)) return 'Nur Kleinbuchstaben, Ziffern und Unterstrich, mit einem Buchstaben am Anfang (höchstens 30 Zeichen).';
  if (own.includes(key)) return `@${key} hat diese Entität schon.`;
  const holder = ctx.story.entities.find((e) => e.id !== exceptId && keysOf(e).includes(key));
  if (holder) {
    const type = ctx.types.get(holder.typeId);
    const title = String(holder.fields[type?.titleKey ?? ''] ?? '').trim() || type?.name || holder.refKey;
    return `@${key} ist schon vergeben (an „${title}“). Bitte wähle einen anderen Schlüssel.`;
  }
  return null;
}

export interface MentionHit {
  nodeId: string;
  /** Textdokument, in dem der Verweis steht */
  docId: string;
  key: string;
  /** Eintrag, auf den der Schlüssel zeigt (null: Schlüssel unbekannt) */
  entityId: string | null;
  /** Lage im Text; bei einem Bezug die ganze Textstelle */
  index: number;
  length: number;
  /** Bezug (die Stelle gehört zum Element) statt Nennung; mit seiner Art */
  span?: { role: SpanRole | null };
}

/** Alle Verweise in allen Textdokumenten der Geschichte. */
export function allMentions(ctx: StoryCtx): MentionHit[] {
  const refs = refIndex(ctx);
  const out: MentionHit[] = [];
  for (const n of ctx.story.nodes) {
    for (const b of n.documents) {
      const spans = spansIn(b.text);
      for (const m of mentionsIn(b.text)) {
        const s = spans.find((x) => m.index > x.textTo && m.index < x.to);
        const where = s ? { index: s.textFrom, length: s.textTo - s.textFrom, span: { role: s.role } } : { index: m.index, length: m.length };
        out.push({ nodeId: n.id, docId: b.id, key: m.key, entityId: refs.get(m.key)?.id ?? null, ...where });
      }
    }
  }
  return out;
}

const KEY = '[A-Za-z][A-Za-z0-9_]{0,29}';
/** Die Formen eines Verweises: verknüpfte Passage, verknüpftes Wort, allein stehender Verweis und (ältere Texte) ein bloßes „@schlüssel“ */
const REF_FORMS = new RegExp(`\\[([^\\]\\n]+)\\]\\(@(${KEY})\\)|([\\p{L}\\p{N}_-])\\[@(${KEY})\\]|\\[@(${KEY})\\]|(^|[^\\w@])@(${KEY})`, 'gu');

/**
 * Verweise eines Textes umhängen (z. B. auf Kopien in einem anderen Projekt): `to` liefert zu einem Schlüssel den neuen oder null – dann
 * wird der Verweis zu normalem Text. Ein verknüpftes Wort oder eine Passage behält ihren Text („Clara[@clara]“ → „Clara“), ein allein
 * stehender Verweis wird zu `label(schlüssel)` (meist der Name des Eintrags).
 */
export function remapRefs(text: string, to: (key: string) => string | null, label: (key: string) => string): string {
  return text.replace(REF_FORMS, (_m, passage?: string, pKey?: string, char?: string, wKey?: string, sKey?: string, pre?: string, bKey?: string) => {
    if (pKey !== undefined) {
      const k = to(pKey.toLowerCase());
      return k ? `[${passage}](@${k})` : passage!;
    }
    if (wKey !== undefined) {
      const k = to(wKey.toLowerCase());
      return k ? `${char}[@${k}]` : char!;
    }
    if (sKey !== undefined) {
      const k = to(sKey.toLowerCase());
      return k ? `[@${k}]` : label(sKey.toLowerCase());
    }
    const k = to(bKey!.toLowerCase());
    return `${pre}${k ? `@${k}` : label(bKey!.toLowerCase())}`;
  });
}

/**
 * Text ohne Verweis-Markierungen, für reine Textanzeigen (Tooltips, Vorschauen): „Lisa[@lisa_wege]“ → „Lisa“,
 * ein allein stehendes „[@lisa_wege]“ → „@lisa_wege“, eine verknüpfte Passage „[Lisa steigt auf den Turm](@lisa_wege)“ und ein Bezug
 * „[„Komm!“]{@lisa_wege rede}“ → ihr Text.
 */
export function plainRefs(text: string): string {
  return stripSpans(text)
    .replace(/\[([^\]\n]+)\]\(@[A-Za-z][A-Za-z0-9_]{0,29}\)/g, '$1')
    .replace(/([\p{L}\p{N}_-])\[@[A-Za-z][A-Za-z0-9_]{0,29}\]/gu, '$1')
    .replace(/\[(@[A-Za-z][A-Za-z0-9_]{0,29})\]/g, '$1');
}
